import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Box, Stack,
    FormControl,
    Autocomplete,
    TextField,
    Typography,
} from "@mui/material";

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import "react-datepicker/dist/react-datepicker.css";
import { Dayjs } from "dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ApiGetClientDbName, ApiGetClientEntities, ApiGetClients, ApiGetStarRankingReportYTD } from "../../../apiService/Core/Apicall.tsx";

interface ClientType {
    clientId: number;
    clientName: string;
}
interface ClientEntity {
    clientEntityId: number;
    entityName: string;
}

const RatingReportYTD: React.FC = () => {
    const [clients, setClients] = useState<any>([]);
    const [clientEntities, setClientEntities] = useState<ClientEntity[]>([]);
    const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
    const [selectedClient, setSelectedClient] = useState<ClientType | null>(null);
    const [clientGroups, setClientGroups] = useState<any[]>([]);
    const [selectedGroupDbName, setSelectedGroupDbName] = useState<any>(null);
    const [startMonth, setStartMonth] = useState<Dayjs | null>(null);
    const [endMonth, setEndMonth] = useState<Dayjs | null>(null);
    const [filteredMonths, setFilteredMonths] = useState<string[]>([]);
    const [reportData, setReportData] = useState<any[]>([]);
    const MAX_MONTHS_DISPLAYED = 12;

    const generateMonths = (newStart: Dayjs | null, newEnd: Dayjs | null) => {
        if (!newStart || !newEnd) return [];

        const months: string[] = [];
        let current = newEnd.startOf("month");

        while ((current.isAfter(newStart) || current.isSame(newStart, "month")) && months.length < MAX_MONTHS_DISPLAYED) {
            months.unshift(current.format("MMM-YY").toUpperCase());
            current = current.subtract(1, "month");
        }

        return months;
    };
    useEffect(() => {
        if (startMonth && endMonth) {
            const months = generateMonths(startMonth, endMonth);
            setFilteredMonths(months);
        }
    }, [startMonth, endMonth]);

    useEffect(() => {
        const getclientgroupName = async () => {

            try {
                const res = await ApiGetClientDbName()
                setClientGroups(res.data)
                setSelectedGroupDbName(res.data.find(item => item.clientGroupName === "MM"))

            } catch (error) {
                alert(error)
            }
        }
        getclientgroupName()
    }, [])

    useEffect(() => {
        const getclients = async () => {
            if (selectedGroupDbName) {

                try {
                    const query = `?status=active&clientGroup=${selectedGroupDbName.clientGroupName}`;
                    const res = await ApiGetClients(query)
                    const mappedClients = res.data.map((client: any) => ({
                        clientId: client.ClientID,
                        clientName: client.ClientName,
                    }));

                    setClients(mappedClients);
                    //   setSelectedClient(mappedClients[0])

                } catch (error) {
                    alert(error)
                }
            }
        }
        getclients()
    }, [selectedGroupDbName])

    useEffect(() => {
        const getcliententities = async () => {
            if (selectedClient) {
                const data = `?clientId=${selectedClient.clientId}`
                try {
                    const res = await ApiGetClientEntities(data)
                    setClientEntities(res.data)
                    //   setSelectedEntityId(res.data[0].clientEntityId)
                } catch (error) {
                    alert(error)
                }
            }
        }
        getcliententities()
    }, [selectedClient])

    const handleClientChange = (event: any, value: ClientType | null) => {
        setSelectedClient(value);
        setClientEntities([]);
        setSelectedEntityId(null);
        setReportData([]);

    };

    useEffect(() => {
        const fetchReportData = async () => {
            if (selectedEntityId && startMonth && endMonth) {
                try {
                    const startMonthValue = startMonth.month() + 1;
                    const startYearValue = startMonth.year();
                    const endMonthValue = endMonth.month() + 1;
                    const endYearValue = endMonth.year();

                    const query = `?clientEntityId=${selectedEntityId}&startMonth=${startMonthValue}&startYear=${startYearValue}&endMonth=${endMonthValue}&endYear=${endYearValue}`;
                    const response = await ApiGetStarRankingReportYTD(query);
                    setReportData(response.data);
                } catch (error) {
                    console.error("Error fetching report data:", error);
                }
            }
        };

        fetchReportData();
    }, [selectedEntityId, startMonth, endMonth]);
    const transformDataForTables = () => {
        if (!reportData.length || !filteredMonths.length) return {
            venueComparison: [],
            categoryComparison: [],
            venueReport: []
        };
        const uniqueCategories = Array.from(new Set(reportData.map(item => item.starRatingDesc)));
        const uniqueVenues = Array.from(new Set(reportData.map(item => item.venueName)));
        const formatMonthKey = (month: number, year: number) => {
            const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
            return `${monthNames[month - 1]}-${year.toString().slice(-2)}`;
        };
        const venueComparisonData = uniqueVenues.map(venue => {
            const venueRatings: Record<string, string> = {};
            filteredMonths.forEach(month => {
                venueRatings[month] = "N/A";
            });
            reportData
                .filter(item => item.venueName === venue)
                .forEach(item => {
                    const monthKey = formatMonthKey(item.reportMonth, item.reportYear);
                    if (filteredMonths.includes(monthKey)) {
                        // If multiple ratings for same venue/month, average them
                        if (venueRatings[monthKey] === "N/A") {
                            venueRatings[monthKey] = item.starRatingScore;
                        } else {
                            const current = parseFloat(venueRatings[monthKey]);
                            const newScore = parseFloat(item.starRatingScore);
                            if (!isNaN(current) && !isNaN(newScore)) {
                                venueRatings[monthKey] = ((current + newScore) / 2).toFixed(2);
                            }
                        }
                    }
                });

            return {
                venue,
                ratings: venueRatings
            };
        });
        const categoryComparisonData = uniqueCategories.map(category => {
            const categoryData = uniqueVenues.map(venue => {
                const venueRatings: Record<string, string> = {};
                filteredMonths.forEach(month => {
                    venueRatings[month] = "N/A";
                });
                reportData
                    .filter(item => item.venueName === venue && item.starRatingDesc === category)
                    .forEach(item => {
                        const monthKey = formatMonthKey(item.reportMonth, item.reportYear);
                        if (filteredMonths.includes(monthKey)) {
                            // If multiple ratings for same venue/category/month, average them
                            if (venueRatings[monthKey] === "N/A") {
                                venueRatings[monthKey] = item.starRatingScore;
                            } else {
                                const current = parseFloat(venueRatings[monthKey]);
                                const newScore = parseFloat(item.starRatingScore);
                                if (!isNaN(current) && !isNaN(newScore)) {
                                    venueRatings[monthKey] = ((current + newScore) / 2).toFixed(2);
                                }
                            }
                        }
                    });

                return {
                    venue,
                    ratings: venueRatings
                };
            });

            return {
                category,
                data: categoryData
            };
        });
        const venueReportData = uniqueVenues.map(venue => {
            const venueData = uniqueCategories.map(category => {
                const categoryRatings: Record<string, string> = {};
                filteredMonths.forEach(month => {
                    categoryRatings[month] = "N/A";
                });
                reportData
                    .filter(item => item.venueName === venue && item.starRatingDesc === category)
                    .forEach(item => {
                        const monthKey = formatMonthKey(item.reportMonth, item.reportYear);
                        if (filteredMonths.includes(monthKey)) {
                            if (categoryRatings[monthKey] === "N/A") {
                                categoryRatings[monthKey] = item.starRatingScore;
                            } else {
                                const current = parseFloat(categoryRatings[monthKey]);
                                const newScore = parseFloat(item.starRatingScore);
                                if (!isNaN(current) && !isNaN(newScore)) {
                                    categoryRatings[monthKey] = ((current + newScore) / 2).toFixed(2);
                                }
                            }
                        }
                    });

                return {
                    category,
                    ratings: categoryRatings
                };
            });

            return {
                venue,
                data: venueData
            };
        });

        return {
            venueComparison: [{ data: venueComparisonData }],
            categoryComparison: categoryComparisonData,
            venueReport: venueReportData
        };
    };
    const calculateOverallAverage = () => {
        if (!reportData.length) return "0.00";

        const validScores = reportData
            .map(item => parseFloat(item.starRatingScore))
            .filter(score => !isNaN(score));

        if (validScores.length === 0) return "0.00";

        const sum = validScores.reduce((a, b) => a + b, 0);
        return (sum / validScores.length).toFixed(2);
    };
    const { venueComparison, categoryComparison, venueReport } = transformDataForTables();
    const overallAverage = calculateOverallAverage();
    return (
        <Box sx={{ p: 4 }} style={{ background: "white" }}>
            <Box>
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h3" textAlign="center">
                        Rating Report-YTD
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "-70px",
                        marginTop: "23px",
                    }}
                >
                    <Stack direction="row" spacing={2}>
                        <FormControl size="small" sx={{ width: 180, mb: 0 }}>
                            <Autocomplete
                                disablePortal
                                id="group-autocomplete"
                                options={clientGroups}
                                value={selectedGroupDbName}
                                getOptionLabel={(option: any) => option.clientGroupName || ""}
                                isOptionEqualToValue={(option: any, value: any) => option.clientGroupDBName === value.clientGroupDBName}
                                onChange={(event, value) => {
                                    setSelectedGroupDbName(value);
                                    setClients([]);
                                    setSelectedClient(null);
                                    setClientEntities([]);
                                    setSelectedEntityId(null);
                                    setReportData([]);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Client Group" />
                                )}
                                size="small"
                            />
                        </FormControl>
                        <FormControl size="small" sx={{ width: 180 }}>
                            <Autocomplete
                                disablePortal
                                id="beacon-autocomplete"
                                options={clients}
                                value={selectedClient}
                                getOptionLabel={(option: ClientType) => option.clientName}
                                isOptionEqualToValue={(option, value) => option.clientId === value.clientId}
                                onChange={handleClientChange}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Client" />
                                )}
                                size="small"
                            />
                        </FormControl>
                        <FormControl size="small" sx={{ width: 185 }}>
                            <Autocomplete
                                disablePortal
                                id="mm-autocomplete"
                                options={clientEntities}

                                getOptionLabel={(option: any) => option.entityName}
                                value={clientEntities.find((entity) => entity.clientEntityId === selectedEntityId) || null}
                                onChange={(event: any, value: ClientEntity | null) => {
                                    setSelectedEntityId(value?.clientEntityId || null);
                                    setReportData([]);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Business" />
                                )}
                                size="small"
                            />
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" sx={{ backgroundColor: "#6b5e34", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#5a522c" } }}>
                            DOWNLOAD AS XLSX
                        </Button>
                        <Button variant="contained" sx={{ backgroundColor: "#6b5e34", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#5a522c" } }}>
                            DOWNLOAD AS PDF
                        </Button>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "20px",
                        gap: "20px",
                        flexWrap: "wrap",
                    }}
                >
                    <Box display="flex" justifyContent="center" mt={1}>
                        <Box>
                            <Stack direction="row" spacing={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        views={['year', 'month']}
                                        label="Start Month"
                                        openTo="month"
                                        value={startMonth}
                                        onChange={(newValue) => {
                                            setStartMonth(newValue);
                                            if (!newValue) {
                                                setEndMonth(null);
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    width: 180,
                                                    '& .MuiInputBase-root': { height: 40 }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>

                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        views={['year', 'month']}
                                        label="End Month"
                                        openTo="month"
                                        value={endMonth}
                                        onChange={(newValue) => setEndMonth(newValue)}
                                        minDate={startMonth || undefined}
                                        disabled={!startMonth}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    width: 180,
                                                    '& .MuiInputBase-root': { height: 40 }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    backgroundColor: "#eaf4d3",
                    border: "2px solid #6b773c",
                    borderRadius: "8px",
                    padding: "20px",
                    height: "70px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    marginTop: "20px",
                }}
            >
                <Typography variant="h5" fontWeight="bold" color="#424d21">
                    Overall Score: {overallAverage}/5
                </Typography>
            </Box>
            <Box sx={{ border: "2px solid #6b773c", borderRadius: "8px", overflow: "hidden", marginTop: "20px" }}>
                <Typography variant="h4" sx={{ textAlign: "center", backgroundColor: "#d9e4c3", py: 2 }}>
                    Venue Comparison Report
                </Typography>
                {filteredMonths.length > 0 && (
                    <Box sx={{ backgroundColor: "#eaf4d3", borderRadius: "8px" }}>
                        {venueComparison.map((categoryData, catIndex) => (
                            <Box key={catIndex}>

                                <Table sx={{ tableLayout: "fixed", minWidth: "100%" }}>
                                    <TableHead>
                                        <TableRow className="table-row-background" sx={{ color: "white" }}>
                                            <TableCell sx={{ width: '20%', fontWeight: "bold", color: "white", position: "sticky", left: 0, zIndex: 2 }}>
                                                Venue Name
                                            </TableCell>
                                            {filteredMonths.map((month) => (
                                                <TableCell key={month} sx={{ fontWeight: "bold", color: "white", textAlign: "center" }}>
                                                    {month}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ fontWeight: "bold", color: "white", textAlign: "center", position: "sticky", right: 0, zIndex: 2 }}>
                                                Average
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categoryData.data.map((row) => {
                                            const filteredRatings = filteredMonths.map((month) => row.ratings[month] ?? "N/A");
                                            const numericValues = filteredRatings
                                                .filter((val) => val !== "N/A")
                                                .map(val => parseFloat(val))
                                                .filter(num => !isNaN(num));
                                            const average = numericValues.length > 0
                                                ? (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2)
                                                : "N/A";
                                            return (
                                                <TableRow key={row.venue}>
                                                    <TableCell sx={{ fontWeight: "bold", position: "sticky", left: 0, backgroundColor: "#ffffff", zIndex: 1 }}>
                                                        {row.venue}
                                                    </TableCell>
                                                    {filteredRatings.map((rating, index) => (
                                                        <TableCell key={index} sx={{ textAlign: "center", backgroundColor: "#ffffff" }}>
                                                            {rating}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: "bold", textAlign: "center", position: "sticky", right: 0, backgroundColor: "#ffffff", zIndex: 1, borderLeft: "3px solid gray" }}>
                                                        {average}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            <Box sx={{ border: "2px solid #6b773c", borderRadius: "8px", overflow: "hidden", marginTop: "20px" }}>
                <Typography variant="h4" sx={{ textAlign: "center", backgroundColor: "#d9e4c3", py: 2 }}>
                    Category Comparison Report
                </Typography>
                {filteredMonths.length > 0 && (
                    <Box sx={{ backgroundColor: "#eaf4d3", borderRadius: "8px" }}>
                        {categoryComparison.map((categoryData, catIndex) => (
                            <Box key={catIndex}>
                                <Typography className="table-row-background" variant="h6" sx={{ color: "white", textAlign: "center", py: 1, fontWeight: "bold", borderBottom: "2px solid white" }}>
                                    {categoryData.category}
                                </Typography>
                                <Table sx={{ tableLayout: "fixed", minWidth: "100%" }}>
                                    <TableHead>
                                        <TableRow className="table-row-background" sx={{ color: "white" }}>
                                            <TableCell sx={{ width: '20%', fontWeight: "bold", color: "white", position: "sticky", left: 0, zIndex: 2 }}>
                                                Venue Name
                                            </TableCell>
                                            {filteredMonths.map((month) => (
                                                <TableCell key={month} sx={{ fontWeight: "bold", color: "white", textAlign: "center" }}>
                                                    {month}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ fontWeight: "bold", color: "white", textAlign: "center", position: "sticky", right: 0, zIndex: 2 }}>
                                                Average
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categoryData.data.map((row) => {
                                            const filteredRatings = filteredMonths.map((month) => row.ratings[month] ?? "N/A");
                                            const numericValues = filteredRatings
                                                .filter((val) => val !== "N/A")
                                                .map(val => parseFloat(val))
                                                .filter(num => !isNaN(num));
                                            const average = numericValues.length > 0
                                                ? (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2)
                                                : "N/A";
                                            return (
                                                <TableRow key={row.venue}>
                                                    <TableCell sx={{ fontWeight: "bold", position: "sticky", left: 0, backgroundColor: "#ffffff", zIndex: 1 }}>
                                                        {row.venue}
                                                    </TableCell>
                                                    {filteredRatings.map((rating, index) => (
                                                        <TableCell key={index} sx={{ textAlign: "center", backgroundColor: "#ffffff" }}>
                                                            {rating}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: "bold", textAlign: "center", position: "sticky", right: 0, backgroundColor: "#ffffff", zIndex: 1, borderLeft: "3px solid gray" }}>
                                                        {average}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
            <Box sx={{ border: "2px solid #6b773c", borderRadius: "8px", overflow: "hidden", marginTop: "20px" }}>
                <Typography variant="h4" sx={{ textAlign: "center", backgroundColor: "#d9e4c3", py: 2 }}>
                    Venue Report
                </Typography>
                {filteredMonths.length > 0 && (
                    <Box sx={{ backgroundColor: "#eaf4d3", borderRadius: "8px" }}>
                        {venueReport.map((venueData, catIndex) => (
                            <Box key={catIndex}>
                                <Typography className="table-row-background" variant="h6" sx={{ color: "white", textAlign: "center", py: 1, fontWeight: "bold", borderBottom: "2px solid white" }}>
                                    {venueData.venue}
                                </Typography>
                                <Table sx={{ tableLayout: "fixed", minWidth: "100%" }}>
                                    <TableHead>
                                        <TableRow className="table-row-background" sx={{ color: "white" }}>
                                            <TableCell sx={{ width: "20%", fontWeight: "bold", color: "white", position: "sticky", left: 0, zIndex: 2 }}>
                                                Category Name
                                            </TableCell>
                                            {filteredMonths.map((month) => (
                                                <TableCell key={month} sx={{ fontWeight: "bold", color: "white", textAlign: "center" }}>
                                                    {month}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ fontWeight: "bold", color: "white", textAlign: "center", position: "sticky", right: 0, zIndex: 2 }}>
                                                Average
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {venueData.data.map((row) => {
                                            const filteredRatings = filteredMonths.map((month) => row.ratings[month] ?? "N/A");
                                            const numericValues = filteredRatings
                                                .filter((val) => val !== "N/A")
                                                .map(val => parseFloat(val))
                                                .filter(num => !isNaN(num));
                                            const average = numericValues.length > 0
                                                ? (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2)
                                                : "N/A";
                                            return (
                                                <TableRow key={row.category}>
                                                    <TableCell sx={{ fontWeight: "bold", position: "sticky", left: 0, backgroundColor: "#ffffff", zIndex: 1 }}>
                                                        {row.category}
                                                    </TableCell>
                                                    {filteredRatings.map((rating, index) => (
                                                        <TableCell key={index} sx={{ textAlign: "center", backgroundColor: "#ffffff" }}>
                                                            {rating}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: "bold", textAlign: "center", position: "sticky", right: 0, backgroundColor: "#ffffff", zIndex: 1, borderLeft: "3px solid gray" }}>
                                                        {average}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default RatingReportYTD;